import { ApiLocation } from "@incendium/api";
import { ArrowForward } from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/material";
import { locationService } from "Apis";
import {
  LocationsAccordionList,
  getPageTypeChanges,
  syncLocationPageTypes,
  updateLocation,
} from "features/location";
import { useLocations, useNotification, useSelectedProject } from "Hooks";
import { useConfirmation } from "Hooks/useConfirmation";
import produce from "immer";
import { useCallback } from "react";

interface IEcomPlatformOnboardingStep5Props {
  onContinue: () => void;
}

function EcomPlatformOnboardingStep5({
  onContinue,
}: IEcomPlatformOnboardingStep5Props) {
  const { selectedProject } = useSelectedProject();
  const { locations, setLocations, loading } = useLocations();
  const handleDelete = useConfirmation();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const onDelete = useCallback(
    (location: ApiLocation) => {
      handleDelete({
        title: "Are you sure you want to delete this location",
        body: "This action can not be undone",
        callback: async () => {
          await locationService.locationServiceDeleteLocation({
            projectId: selectedProject!.id as number,
            locationId: location.id as number,
          });

          setLocations(
            produce(locations, (draft) => {
              const idx = locations.findIndex((l) => l.id === location.id);
              if (idx !== -1) {
                draft.splice(idx, 1);
              }
            }),
          );

          return `${location.name} Deleted`;
        },
      });
    },
    [handleDelete, locations, selectedProject, setLocations],
  );

  const onSave = useCallback(
    async (location: ApiLocation) => {
      try {
        const originalLocation = locations.find((l) => l.id === location.id);

        const res = await updateLocation(
          selectedProject!.id as number,
          location.id as number,
          location,
        );

        if (originalLocation) {
          const { toAdd, toRemove } = getPageTypeChanges(
            originalLocation.pageTypes || [],
            location.pageTypes || [],
          );

          if (toAdd.length > 0 || toRemove.length > 0) {
            await syncLocationPageTypes(
              selectedProject!.id as number,
              location.id as number,
              originalLocation.pageTypes || [],
              location.pageTypes || [],
            );
          }
        }

        setLocations(
          produce(locations, (draft) => {
            const idx = locations.findIndex((l) => l.id === location.id);
            if (idx !== -1) {
              draft[idx] = res as ApiLocation;
            }
          }),
        );
        showSuccessNotification(`${location.name} Updated`);
      } catch {
        showErrorNotification(`Failed to update ${location.name}`);
      }
    },
    [
      locations,
      setLocations,
      selectedProject,
      showSuccessNotification,
      showErrorNotification,
    ],
  );

  return (
    <Stack
      spacing={3}
      sx={{ height: "100%", minHeight: 0, overflow: "hidden" }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <LocationsAccordionList
          locations={locations}
          loading={loading}
          title="Detected page groups"
          subtitle="We found likely groupings from your site structure. Review and adjust if needed."
          onSave={onSave}
          onDelete={onDelete}
          scrollable
        />
      </Box>
      <Button
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        endIcon={<ArrowForward />}
        onClick={onContinue}
        sx={{ flexShrink: 0 }}
      >
        Continue
      </Button>
    </Stack>
  );
}

export default EcomPlatformOnboardingStep5;
